import type { PhotoSource } from '@fastgarage/protocol';
import type { UploadContentType } from './capture';

/**
 * In-memory FIFO upload queue with exponential backoff retry
 * (1s → 2s → 4s → … capped at 30s, retrying forever while the session is
 * active). Survives network drops; a browser `online` event triggers an
 * immediate retry. Pure logic — all I/O is injected, so it is unit-testable
 * with fake timers.
 */

export interface PhotoJob {
  /** requestId of the triggering command; echoed in PHOTO_DONE / PHOTO_ERROR. */
  requestId: string;
  blob: Blob;
  contentType: UploadContentType;
  source: PhotoSource;
  width: number;
  height: number;
  /** Hex SHA-256 of the blob, computed before enqueueing. */
  sha256: string;
}

export interface UploadStatus {
  /** Successfully uploaded photos (presign + PUT + complete all done). */
  transferred: number;
  /** Jobs still waiting or retrying. */
  queued: number;
  /** Jobs dropped on permanent errors (e.g. session ended mid-upload). */
  failed: number;
}

export interface UploadQueueOptions {
  /** Runs the full pipeline (presign → PUT → complete); throws on failure. */
  uploadJob: (job: PhotoJob) => Promise<{ photoId: string }>;
  /** Called after a successful upload (used to send PHOTO_DONE). */
  onJobDone?: (job: PhotoJob, photoId: string) => void;
  /** Called on every transferred/queued change (upload status chip). */
  onStatus?: (status: UploadStatus) => void;
  /** Errors for which retrying is pointless (e.g. session already ended) —
   * the job is dropped instead of retrying forever. Default: retry everything. */
  isPermanentError?: (error: unknown) => boolean;
  baseDelayMs?: number;
  maxDelayMs?: number;
  /** Default: listen for window 'online' when a window exists. */
  registerOnlineListener?: boolean;
}

const DEFAULT_BASE_DELAY_MS = 1_000;
const DEFAULT_MAX_DELAY_MS = 30_000;

export class UploadQueue {
  private readonly jobs: PhotoJob[] = [];
  private transferred = 0;
  private failed = 0;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private stopped = false;
  private readonly onlineListenerActive: boolean;
  private readonly onOnline = (): void => {
    this.retryNow();
  };

  constructor(private readonly options: UploadQueueOptions) {
    this.onlineListenerActive =
      (options.registerOnlineListener ?? true) && typeof window !== 'undefined';
    if (this.onlineListenerActive) {
      window.addEventListener('online', this.onOnline);
    }
  }

  get status(): UploadStatus {
    return { transferred: this.transferred, queued: this.jobs.length, failed: this.failed };
  }

  enqueue(job: PhotoJob): void {
    if (this.stopped) return;
    this.jobs.push(job);
    this.emitStatus();
    void this.pump();
  }

  /** Cancels a pending backoff delay and retries immediately. */
  retryNow(): void {
    if (this.stopped) return;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    void this.pump();
  }

  /** Stops processing permanently (session teardown). */
  stop(): void {
    this.stopped = true;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.onlineListenerActive) {
      window.removeEventListener('online', this.onOnline);
    }
  }

  private emitStatus(): void {
    this.options.onStatus?.(this.status);
  }

  private async pump(): Promise<void> {
    if (this.running || this.stopped || this.timer !== null) return;
    this.running = true;
    try {
      while (!this.stopped && this.jobs.length > 0) {
        const job = this.jobs[0];
        if (!job) break;
        try {
          const { photoId } = await this.options.uploadJob(job);
          this.jobs.shift();
          this.attempt = 0;
          this.transferred += 1;
          this.emitStatus();
          this.options.onJobDone?.(job, photoId);
        } catch (error) {
          if (this.stopped) break;
          if (this.options.isPermanentError?.(error)) {
            // Retrying cannot succeed (session ended/deleted) — drop the job.
            this.jobs.shift();
            this.failed += 1;
            this.attempt = 0;
            this.emitStatus();
            continue;
          }
          this.attempt += 1;
          const base = this.options.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
          const max = this.options.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
          const delay = Math.min(base * 2 ** (this.attempt - 1), max);
          this.timer = setTimeout(() => {
            this.timer = null;
            void this.pump();
          }, delay);
          break;
        }
      }
    } finally {
      this.running = false;
    }
  }
}
